import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import { delay } from '../../../Utility/Task/TaskQueue';
import ColorFilter from '../../../Component/ColorFilter';
import Point from '../../../Utility/Point';
import CardSystem from '../../Card/CardSystem';
import Container from '../../../Prototype/Container';
import { showCardScoreEffect } from '../CardAnimationHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import { StartAnimation } from '../AnimationHelper';
import { CARD_STACK_OFFSET } from '../../../Constants/InGame';
import CardComponent from '../../../Component/Level/Card';

interface ICardPlusThreeMeta {
    system: CardSystem;
    sourceCard: BaseCard;
    targetCards: BaseCard[];
    cardsContainer: Container;
    stackContainer: GameObject;
}

export class CardPlusThree extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardPlusThreeMeta
    ): IterableIterator<number> {
        animator.addChild(meta.sourceCard);

        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        const plusThreeEffect = GameObject.Instantiate(Container);
        meta.sourceCard.addChild(plusThreeEffect);
        animator.addChild(meta.sourceCard);
        animator.addChild(plusThreeEffect);

        StartAnimation(plusThreeEffect, 'BoomExplosionEffect');

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        meta.targetCards.forEach((card) => {
            card.getComponent(CardComponent).skipStackAlign = true;
        });

        yield* delay(500);

        const newCardPositions = [
            { x: -125, y: 50, rotation: -Math.PI * 0.15 },
            { x: 0, y: -130, rotation: 0 },
            { x: 125, y: 50, rotation: Math.PI * 0.15 },
        ];

        const cardOutDuration = 300;
        const cardInDuration = 600;

        for (let i = 0; i < meta.targetCards.length; i++) {
            const newCard = meta.targetCards[i];

            newCard.getComponent(Renderable).visible = true;

            newCard.addComponent(ColorFilter, {
                brightness: 10,
            });

            // Add as child to animator first for relative positioning.
            animator.addChild(newCard);

            const newCardTransform = newCard.getComponent(TransformComponent);
            newCardTransform.position.copy(
                sourceCardTransform.position as Point
            );

            // Add as child to stack container.
            meta.stackContainer.addChild(newCard);

            const tweenPosition = this.getTween(newCardTransform.position);
            const tweenRotation = this.getTween(newCardTransform);

            tweenPosition
                .to(
                    {
                        x: newCardTransform.position.x + newCardPositions[i].x,
                        y: newCardTransform.position.y + newCardPositions[i].y,
                    },
                    cardOutDuration
                )
                .easing(Easing.Cubic.Out)
                .start(this.elapsedTime);

            tweenRotation
                .to(
                    {
                        rotation: newCardPositions[i].rotation,
                    },
                    cardOutDuration
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            const tweenBrightness = this.getTween(
                newCard.getComponent(ColorFilter)
            );
            tweenBrightness
                .to(
                    {
                        brightness: 1,
                    },
                    cardOutDuration + 100
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);
        }

        yield* delay(cardOutDuration);

        for (let i = 0; i < meta.targetCards.length; i++) {
            const newCard = meta.targetCards[i];
            const newCardTransform = newCard.getComponent(TransformComponent);

            const tweenPosition = this.getTween(newCardTransform.position);
            const tweenRotation = this.getTween(newCardTransform);

            tweenPosition
                .to(
                    {
                        x: CARD_STACK_OFFSET,
                        y: 0,
                    },
                    cardInDuration
                )
                .delay(200 + i * 100)
                .easing(Easing.Cubic.InOut)
                .start(this.elapsedTime);

            tweenRotation
                .to(
                    {
                        rotation: 0,
                    },
                    cardInDuration
                )
                .delay(200 + i * 100)
                .easing(Easing.Quadratic.InOut)
                .onComplete(() => {
                    newCard.getComponent(CardComponent).skipStackAlign = false;
                    this.emit('PlusThreeCardAdded', newCard, i);
                })
                .start(this.elapsedTime);

            showCardScoreEffect(meta.system.scene, newCard);
        }

        yield* delay(cardInDuration + 500);
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }
}
